import React, { useState } from "react";
import { siteConfig } from "../Datas/productData";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  orderNumber: "",
  message: "",
};

const subjects = [
  "Product information",
  "Order support",
  "Delivery and returns",
  "Press and partnerships",
  "Something else",
];

const getFieldError = (name, value) => {
  const cleanValue = value.trim();

  switch (name) {
    case "firstName":
      return cleanValue.length >= 2 ? "" : "Please enter your first name.";
    case "lastName":
      return cleanValue.length >= 2 ? "" : "Please enter your last name.";
    case "email":
      if (!cleanValue) return "Please enter your email address.";
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanValue)
        ? ""
        : "Please enter a valid email address.";
    case "phone":
      if (!cleanValue) return "";
      return /^[+()\d\s.-]{7,}$/.test(cleanValue)
        ? ""
        : "Please enter a valid phone number.";
    case "subject":
      return cleanValue ? "" : "Please select a subject.";
    case "message":
      if (!cleanValue) return "Please tell us how we can help.";
      return cleanValue.length >= 20
        ? ""
        : "Please add a little more detail (at least 20 characters).";
    default:
      return "";
  }
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { contact, brandName } = siteConfig;

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const nextErrors = { ...current };
      delete nextErrors[name];
      return nextErrors;
    });
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const fieldError = getFieldError(name, value);

    setErrors((current) => {
      const nextErrors = { ...current };
      if (fieldError) nextErrors[name] = fieldError;
      else delete nextErrors[name];
      return nextErrors;
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    Object.entries(formData).forEach(([name, value]) => {
      const fieldError = getFieldError(name, value);
      if (fieldError) nextErrors[name] = fieldError;
    });

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      const firstInvalidField = document.getElementById(
        Object.keys(formErrors)[0]
      );
      firstInvalidField?.focus();
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    await new Promise((resolve) => window.setTimeout(resolve, 650));

    setFormData(initialFormData);
    setIsSubmitting(false);
    setShowSuccess(true);
  };

  const renderError = (name) =>
    errors[name] ? (
      <span className="contact__error" id={`${name}-error`} role="alert">
        {errors[name]}
      </span>
    ) : null;

  return (
    <main className="contact" aria-labelledby="contact-title">
      <div className="contact__layout">
        <aside className="contact__introduction">
          <p className="contact__eyebrow">Contact / The studio</p>
          <h1 id="contact-title">Let’s begin a conversation.</h1>
          <p className="contact__lead">
            Questions about a piece, an order or the collection? The
            {` ${brandName} `}
            team is here to help.
          </p>

          <address className="contact__details">
            <div>
              <span>Email</span>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </div>

            <div>
              <span>Studio</span>
              <p>{contact.address}</p>
            </div>
            <div>
              <span>Client care</span>
              <p>{contact.hours}</p>
            </div>
          </address>
        </aside>

        <section className="contact__form-panel" aria-label="Contact form">
          {showSuccess ? (
            <div
              className="contact__success"
              id="contact-success"
              role="status"
              aria-live="polite"
            >
              <span className="contact__success-mark" aria-hidden="true">
                ✓
              </span>
              <p className="contact__eyebrow">Message received</p>
              <h2>Thank you for writing.</h2>
              <p>
                Your note is with our client care team. We will reply as soon as
                possible during studio hours.
              </p>
              <button type="button" onClick={() => setShowSuccess(false)}>
                Send another message
                <span aria-hidden="true">↗</span>
              </button>
            </div>
          ) : (
            <form className="contact__form" onSubmit={handleSubmit} noValidate>
              <div className="contact__form-heading">
                <p className="contact__eyebrow">Send a note</p>
                <p>Required fields are marked with an asterisk.</p>
              </div>

              <div className="contact__fields contact__fields--split">
                <div className="contact__field">
                  <label htmlFor="firstName">First name *</label>
                  <input
                    autoComplete="given-name"
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.firstName)}
                    aria-describedby={
                      errors.firstName ? "firstName-error" : undefined
                    }
                  />
                  {renderError("firstName")}
                </div>

                <div className="contact__field">
                  <label htmlFor="lastName">Last name *</label>
                  <input
                    autoComplete="family-name"
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.lastName)}
                    aria-describedby={
                      errors.lastName ? "lastName-error" : undefined
                    }
                  />
                  {renderError("lastName")}
                </div>
              </div>

              <div className="contact__fields contact__fields--split">
                <div className="contact__field">
                  <label htmlFor="email">Email address *</label>
                  <input
                    autoComplete="email"
                    id="email"
                    inputMode="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {renderError("email")}
                </div>

                <div className="contact__field">
                  <label htmlFor="phone">Telephone (optional)</label>
                  <input
                    autoComplete="tel"
                    id="phone"
                    inputMode="tel"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? "phone-error" : undefined}
                  />
                  {renderError("phone")}
                </div>
              </div>

              <div className="contact__fields contact__fields--split">
                <div className="contact__field">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(errors.subject)}
                    aria-describedby={
                      errors.subject ? "subject-error" : undefined
                    }
                  >
                    <option value="">Choose a subject</option>
                    {subjects.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                  {renderError("subject")}
                </div>

                <div className="contact__field">
                  <label htmlFor="orderNumber">Order number (optional)</label>
                  <input
                    autoComplete="off"
                    id="orderNumber"
                    name="orderNumber"
                    type="text"
                    value={formData.orderNumber}
                    onChange={handleInputChange}
                    placeholder="e.g. MC-1048"
                  />
                </div>
              </div>

              <div className="contact__field contact__field--message">
                <label htmlFor="message">Your message *</label>
                <textarea
                  id="message"
                  maxLength="700"
                  name="message"
                  rows="7"
                  value={formData.message}
                  onBlur={handleBlur}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={
                    errors.message
                      ? "message-error message-count"
                      : "message-count"
                  }
                />
                <div className="contact__message-meta">
                  {renderError("message")}
                  <span id="message-count" aria-live="polite">
                    {formData.message.length} / 700
                  </span>
                </div>
              </div>

              <button
                className="contact__submit"
                type="submit"
                disabled={isSubmitting}
              >
                <span>{isSubmitting ? "Sending…" : "Send message"}</span>
                <span aria-hidden="true">↗</span>
              </button>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}

export default Contact;
